import DataTable from 'react-data-table-component';
import { useState, useEffect } from "react";
import { LOG_RECORDS_MANAGER } from '../components/log-records-manager';
import { TOAST_FAIL } from '../components/toasters';
import Loader from "../templates/loader-template";

function VehicleLogList({ onRefresh }) {
    const { VEHICLE_LOGS_FETCH, ARCHIVE_VEHICLE_LOGS, VEHICLE_DELETE_LOG } = LOG_RECORDS_MANAGER();
    const [vehicleLogs, setVehicleLogs] = useState([]);
    const [filterText, setFilterText] = useState("");  // State for the filter text
    const [searchText, setSearchText] = useState("");
    const [loader, setLoader] = useState("Archive");
    const userType = localStorage.getItem("user_type");

    const columns = [
        {
            name: 'Plate Number',
            selector: row => {
                const plate = String(row.plate_number);
                if (userType === "Admin") {
                    return plate;  // Show full plate number for Admin
                } else {
                    const middle = plate.slice(1, -1).replace(/./g, '*');
                    return `${plate[0]}${middle}${plate.slice(-1)}`;  // Mask for non-admin users
                }
            },
            sortable: true,
            width: '10%',
        },
        {
            name: 'Owner Name',
            selector: row => row.username,
            sortable: false,
            width: '10%',
        },
        {
            name: 'Email',
            selector: row => userType === "Admin" ? row.email : maskEmail(row.email),
            sortable: false,
            width: '20%',
        },
        {
            name: 'Identity',
            selector: row => row.identity,
            sortable: true,
            width: '10%',
        },
        {
            name: "Time In",
            selector: row => formatDateTime(row.time_in),
            sortable: true,
            width: '25%',
        },
        {
            name: "Time Out",
            selector: row => formatDateTime(row.time_out), // Otherwise, format the time_out value
            sortable: true,
            width: '25%',
        }
    ];

    const customStyles = {
        table : {
            style : {
                height: '85vh',
            }
        },
        headCells: {
            style: {
                fontWeight: 'bold',  // Make headers bold
                backgroundColor: '#191D80',  // Background color for the headers
                color: '#fff',  // Font color for the headers
                paddingLeft: '2.5%',
                justifyContent: 'center',
            },
        },
        cells: {
            style: {
                justifyContent: 'center',
            }
        }
    };

    const maskEmail = (email) => {
        const [localPart, domain] = email.split("@");
        return `${localPart[0]}***@${domain}`;
    };

    // Helper function to format date
    const formatDateTime = (dateString) => {
        if (!dateString) {
            return "N/A";
        }
    
        // Convert to a Date object
        const date = new Date(dateString);
    
        // Format the date with options
        const options = { 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric', 
            hour: 'numeric', 
            minute: 'numeric', 
            hour12: true,
            timeZone: 'Asia/Manila' // Explicitly set the Manila timezone
        };
    
        return new Intl.DateTimeFormat('en-US', options).format(date);
    };
    

    // Fetch all vehicle logs data
    const fetchData = async () => {
        const vehicleLogList = await VEHICLE_LOGS_FETCH();
        const sortedLogs = vehicleLogList.sort((a, b) => new Date(b.time_in) - new Date(a.time_in));
        setVehicleLogs(sortedLogs);
    };

    useEffect(() => {
        fetchData();
        VEHICLE_DELETE_LOG();
    }, [onRefresh]);

    // Filtered data based on input
    const filteredLogs = vehicleLogs.filter(log => {
        // **Date Filtering**
        let matchesDate = true;
        if (filterText) {
            const [year, month, day] = filterText.split('-');
            const formattedFilterDate = new Date(`${year}-${month}-${day}`);
            const filterDateString = new Intl.DateTimeFormat('en-US', {
                year: 'numeric', month: 'long', day: 'numeric'
            }).format(formattedFilterDate);
    
            const formatDateToLong = (dateString) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                return new Intl.DateTimeFormat('en-US', {
                    year: 'numeric', month: 'long', day: 'numeric'
                }).format(date);
            };
    
            const timeInDate = formatDateToLong(log.time_in);
    
            matchesDate = timeInDate === filterDateString;
        }
    
        // **Search Filtering**
        const matchesSearch = searchText === "" ||
            log.plate_number.toLowerCase().includes(searchText.toLowerCase()) ||
            log.username?.toLowerCase().includes(searchText.toLowerCase()) ||
            log.identity.toLowerCase().includes(searchText.toLowerCase()) ||
            log.email.toLowerCase().includes(searchText.toLowerCase());
    
        return (filterText ? matchesDate : true) && matchesSearch; // Both filters must match
    });

    const submit = async(event) => {
        event.preventDefault();
        setLoader("Archiving...");

        if(filterText === "") {
            TOAST_FAIL("Please filter first the log record you want to archive");
        } else {
            await ARCHIVE_VEHICLE_LOGS(filterText);     
        }
        setLoader("Archive");
        fetchData();
    }


    return (
        <div className="logs-table">
            <DataTable
                columns={columns}
                data={filteredLogs}  // Use the filtered data
                customStyles={customStyles}
                fixedHeader
                pagination
                responsive
                subHeader
                noDataComponent="No Logs to display"
                subHeaderComponent={(
                    <div className="d-flex justify-content-between w-100 align-items-center">
                        <h5 className="fw-bold">Vehicle Logs</h5>

                        <div>
                            <input className="log-search-field ps-2" 
                                   placeholder="Search"
                                   value={searchText} 
                                   onChange={e => setSearchText(e.target.value)} />
                        </div>

                        <div className="d-flex align-items-center">
                            <button 
                                className="btn btn-danger me-2" 
                                onClick={ submit }
                                disabled={loader === "Archiving..."}>{ loader }</button>
                            <input
                                type="date"
                                className="border-2 p-1 rounded-3 bg-transparent ps-2"
                                placeholder="Filter logs"
                                value={filterText}
                                onChange={e => setFilterText(e.target.value)}  // Automatically filter data
                            />
                        </div>
                    </div>
                )}
            />
        </div>
    );
}

export default VehicleLogList;